import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../utils/api'
import logger from '../utils/logger'

/**
 * Fetches current promo banner state for the authenticated user.
 *
 * Flow:
 *  1. Mount → GET /api/promo/banner → { type, promoCode, discount, expiresAt, ... }
 *  2. If banner exists → POST /api/promo/banner/shown (starts 48h urgency for comeback)
 *  3. handleCta → navigate to /subscription?promo=CODE
 *  4. handleDismiss → POST /api/promo/banner/dismiss (hides for 24h)
 *
 * Backend handles all eligibility logic (new user, returning, urgency, dismissal cooldown).
 * Frontend is just a dumb renderer.
 */
export function usePromoBanner() {
  const [banner, setBanner] = useState(null)
  const navigate = useNavigate()

  const fetchBanner = useCallback(async () => {
    try {
      const res = await api.get('/api/promo/banner')
      const data = res.data?.data || null
      setBanner(data)

      // Mark as shown on first impression (backend dedupes via shownAt check)
      if (data?.type) {
        api.post('/api/promo/banner/shown', { type: data.type }).catch(() => {})
      }
    } catch (err) {
      logger.debug('Promo banner fetch error:', err.message)
      setBanner(null)
    }
  }, [])

  useEffect(() => {
    fetchBanner()
  }, [fetchBanner])

  const handleCta = useCallback(() => {
    if (!banner) return
    navigate(`/subscription?promo=${banner.promoCode}`)
  }, [banner, navigate])

  const handleDismiss = useCallback(async () => {
    if (!banner) return
    const type = banner.type
    setBanner(null) // optimistic — hide immediately
    try {
      await api.post('/api/promo/banner/dismiss', { type })
    } catch (err) {
      logger.debug('Promo banner dismiss error:', err.message)
    }
  }, [banner])

  return { banner, handleCta, handleDismiss }
}

export default usePromoBanner
